import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  Camera, CheckCircle2, XCircle, ArrowLeft, Loader2,
  LogIn, LogOut, Coffee, Shield, Lock, Sparkles, AlertTriangle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Locked wall-mount iPad kiosk with face-verified punch.
 * URL: /kiosk-secure  (or /paypunch-kiosk)
 *
 * Screens:
 *   1. IDLE           — big greeting, tap "Start"
 *   2. CODE_ENTRY     — number pad, employee taps their code
 *   3. PUNCH_TYPE     — pick In / Out / Break Start / Break End
 *   4. CAPTURING      — auto-open camera, capture 1 selfie, submit
 *   5. VERIFYING      — Gemini match in progress
 *   6. SUCCESS/FAIL   — green tick or red block
 *   7. OVERRIDE       — manager PIN modal on fail
 *
 * Kiosk protections:
 *   - No back arrow, no nav
 *   - Fullscreen API on first user interaction (iOS requires the tap)
 *   - Manager PIN required to exit (bottom-right corner tap 5x)
 */

const SCREENS = { IDLE: "idle", CODE: "code", TYPE: "type", CAM: "cam", VERIFY: "verify", RESULT: "result" };

export default function KioskSecurePage() {
  const [screen, setScreen] = useState(SCREENS.IDLE);
  const [code, setCode] = useState("");
  const [punchType, setPunchType] = useState(null);
  const [result, setResult] = useState(null);
  const [tenant, setTenant] = useState({ name: "PayPunch" });
  const [clock, setClock] = useState(new Date());
  const [exitTapCount, setExitTapCount] = useState(0);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  // Test mode: /kiosk-test route OR ?test=1 query param
  const isTestMode =
    typeof window !== "undefined" &&
    (window.location.pathname.includes("kiosk-test") ||
      new URLSearchParams(window.location.search).get("test") === "1");

  useEffect(() => {
    axios.get(`${API}/tenant/current`).then((r) => {
      if (r.data?.exists) setTenant({ name: r.data.name });
    }).catch(() => {});
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const goFullscreen = () => {
    try {
      const el = document.documentElement;
      if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
      else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    } catch {}
  };

  const start = () => {
    goFullscreen();
    setCode("");
    setPunchType(null);
    setResult(null);
    setScreen(SCREENS.CODE);
  };

  const validateCode = async () => {
    // Master PINs don't punch — they unlock the master dashboard on /timecard.
    // Detect them here and point the user to the right place instead of a
    // confusing "not a valid employee" error.
    const MASTER_PINS = ["1111", "83528", "4817"];
    if (MASTER_PINS.includes(code)) {
      toast.error("That's a Master PIN, not an employee code. Master mode lives at /timecard.", { duration: 5000 });
      setCode("");
      return;
    }
    try {
      const res = await axios.get(`${API}/kiosk-face/check-code/${code}`);
      if (!res.data?.exists) {
        toast.error(`Code ${code} isn't an employee code. Ask your manager for your 4-digit code.`);
        setCode("");
        return;
      }
      if (!res.data.active) {
        toast.error(`${res.data.name} is inactive — see manager`);
        setCode("");
        return;
      }
      if (!res.data.enrolled) {
        toast.error(`${res.data.name} not enrolled for face verification. Ask manager to enroll.`);
        setCode("");
        return;
      }
      setScreen(SCREENS.TYPE);
    } catch (e) {
      toast.error("Couldn't validate code — try again");
    }
  };

  const reset = (afterMs = 3000) => {
    setTimeout(() => {
      setScreen(SCREENS.IDLE);
      setCode("");
      setPunchType(null);
      setResult(null);
    }, afterMs);
  };

  const submitPunch = async (selfieB64) => {
    setScreen(SCREENS.VERIFY);
    try {
      const res = await axios.post(`${API}/kiosk-face/punch`, {
        code,
        photo_base64: selfieB64,
        punch_type: punchType,
        test_mode: isTestMode,
      });
      setResult({ ...res.data, selfie: selfieB64 });
      setScreen(SCREENS.RESULT);
      if (res.data.success) reset(3000);
    } catch (e) {
      const detail = e.response?.data?.detail || "Punch failed";
      setResult({ success: false, blocked: true, message: detail, selfie: selfieB64 });
      setScreen(SCREENS.RESULT);
    }
  };

  // Manager exit — tap top-right corner 5 times
  const handleCornerTap = () => {
    setExitTapCount((c) => {
      const next = c + 1;
      if (next >= 5) {
        setShowExitModal(true);
        return 0;
      }
      setTimeout(() => setExitTapCount(0), 3000);
      return next;
    });
  };

  return (
    <div className={`min-h-screen text-white select-none overscroll-none ${isTestMode ? "bg-gradient-to-br from-[#3B2F0F] via-[#4A2E10] to-[#5B2A08]" : "bg-gradient-to-br from-[#0F1013] via-[#1A1620] to-[#2A0E1E]"}`}>
      {isTestMode && (
        <div className="bg-amber-400 text-black text-center py-1.5 font-black tracking-widest text-xs uppercase">
          🧪 TEST MODE — Punches go to sandbox · Real payroll untouched
        </div>
      )}
      {/* Hidden exit tap */}
      <button
        className="absolute top-0 right-0 w-16 h-16 z-50 opacity-0"
        onClick={handleCornerTap}
        aria-label="Manager exit"
        data-testid="kiosk-exit-corner"
      />

      {/* Live clock header */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-white/10 bg-black/40">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A61B1B] to-[#5B0F0F] flex items-center justify-center shadow-lg">
            <svg viewBox="0 0 40 40" className="w-7 h-7" fill="none" stroke="#FBBF24" strokeWidth="3" strokeLinecap="round">
              <circle cx="20" cy="18" r="12" />
              <path d="M20 10 L20 18 L26 22" />
            </svg>
          </div>
          <div>
            <div className="font-black text-lg leading-none flex items-center gap-2">
              PayPunch <Lock className="w-3.5 h-3.5 text-amber-300" />
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-amber-300 font-bold mt-1">Powered by AV Tech</div>
          </div>
        </div>
        <div className="text-center">
          <div className="text-white/60 text-xs">{tenant.name}</div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black tabular-nums">{clock.toLocaleTimeString()}</div>
          <div className="text-[11px] text-white/60">{clock.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}</div>
        </div>
      </header>

      <main className="flex items-center justify-center min-h-[calc(100vh-80px)] p-4">
        {screen === SCREENS.IDLE && <IdleScreen onStart={start} tenant={tenant} />}
        {screen === SCREENS.CODE && <CodeScreen code={code} setCode={setCode} onNext={validateCode} onCancel={() => setScreen(SCREENS.IDLE)} />}
        {screen === SCREENS.TYPE && <TypeScreen onPick={(t) => { setPunchType(t); setScreen(SCREENS.CAM); }} onBack={() => setScreen(SCREENS.CODE)} />}
        {screen === SCREENS.CAM && <CaptureScreen onCapture={submitPunch} onCancel={() => setScreen(SCREENS.TYPE)} />}
        {screen === SCREENS.VERIFY && <VerifyingScreen />}
        {screen === SCREENS.RESULT && (
          <ResultScreen
            result={result}
            code={code}
            punchType={punchType}
            onRetry={start}
            onOverride={() => setShowOverride(true)}
          />
        )}
      </main>

      {showOverride && (
        <ManagerOverrideModal
          code={code}
          punchType={punchType}
          selfie={result?.selfie}
          onDone={() => { setShowOverride(false); reset(2500); }}
          onClose={() => setShowOverride(false)}
        />
      )}
      {showExitModal && (
        <ExitModal onClose={() => setShowExitModal(false)} />
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Screens
// ═══════════════════════════════════════════════════════════════

function IdleScreen({ onStart, tenant }) {
  return (
    <div className="text-center max-w-lg">
      {/* PayPunch wordmark logo */}
      <div className="mb-4">
        <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-[#A61B1B] to-[#5B0F0F] shadow-xl">
          <div className="w-14 h-14 rounded-xl bg-white/15 flex items-center justify-center backdrop-blur">
            <svg viewBox="0 0 40 40" className="w-9 h-9" fill="none" stroke="#FBBF24" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="20" cy="18" r="12" />
              <path d="M20 10 L20 18 L26 22" />
            </svg>
          </div>
          <div className="text-left">
            <div className="text-white text-3xl font-black leading-none tracking-tight">PayPunch</div>
            <div className="text-amber-300 text-[10px] font-bold uppercase tracking-[0.25em] mt-1">Powered by AV Tech</div>
          </div>
        </div>
      </div>
      <h1 className="text-5xl font-black mb-2">Welcome</h1>
      <p className="text-white/60 mb-8 text-lg">Tap below to clock in with face verification</p>
      <Button
        onClick={onStart}
        data-testid="kiosk-start"
        className="bg-amber-400 hover:bg-amber-500 text-black text-2xl font-black h-20 px-16 rounded-2xl shadow-lg"
      >
        <Camera className="w-8 h-8 mr-3" /> START
      </Button>
      <p className="text-white/40 text-xs mt-6 flex items-center justify-center gap-1">
        <Shield className="w-3 h-3" /> Anti-buddy-punch · Face + Code required
      </p>
    </div>
  );
}

function CodeScreen({ code, setCode, onNext, onCancel }) {
  const push = (d) => setCode((c) => (c.length < 6 ? c + d : c));
  const back = () => setCode((c) => c.slice(0, -1));
  const digits = ["1","2","3","4","5","6","7","8","9"];
  return (
    <div className="text-center max-w-md w-full">
      <h2 className="text-3xl font-black mb-2">Enter your employee code</h2>
      <div className="mt-6 h-20 flex items-center justify-center gap-3 bg-white/5 border border-white/10 rounded-2xl">
        <span className="text-5xl font-black tracking-widest tabular-nums">
          {code.padEnd(4, "•").split("").map((c, i) => (
            <span key={i} className={c === "•" ? "text-white/20" : "text-amber-300"}>{c}</span>
          ))}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3 mt-6">
        {digits.map((d) => (
          <button
            key={d}
            onClick={() => push(d)}
            data-testid={`kiosk-key-${d}`}
            className="h-20 rounded-2xl bg-white/10 hover:bg-white/20 active:bg-white/30 text-3xl font-black transition"
          >{d}</button>
        ))}
        <button onClick={back} data-testid="kiosk-key-back" className="h-20 rounded-2xl bg-white/10 hover:bg-white/20 text-lg font-bold">DEL</button>
        <button onClick={() => push("0")} data-testid="kiosk-key-0" className="h-20 rounded-2xl bg-white/10 hover:bg-white/20 text-3xl font-black">0</button>
        <button
          onClick={onNext}
          disabled={code.length < 4}
          data-testid="kiosk-key-next"
          className="h-20 rounded-2xl bg-amber-400 hover:bg-amber-500 disabled:opacity-30 text-black text-lg font-black transition"
        >GO →</button>
      </div>
      <Button variant="ghost" onClick={onCancel} className="mt-6 text-white/60">Cancel</Button>
    </div>
  );
}

function TypeScreen({ onPick, onBack }) {
  const opts = [
    { id: "in", label: "Punch IN", icon: LogIn, color: "from-emerald-400 to-emerald-600" },
    { id: "break_start", label: "Break START", icon: Coffee, color: "from-amber-400 to-amber-600" },
    { id: "break_end", label: "Break END", icon: Coffee, color: "from-blue-400 to-blue-600" },
    { id: "out", label: "Punch OUT", icon: LogOut, color: "from-red-400 to-red-600" },
  ];
  return (
    <div className="text-center max-w-2xl w-full">
      <h2 className="text-3xl font-black mb-6">What are you punching?</h2>
      <div className="grid grid-cols-2 gap-4">
        {opts.map((o) => (
          <button
            key={o.id}
            onClick={() => onPick(o.id)}
            data-testid={`kiosk-punch-${o.id}`}
            className={`bg-gradient-to-br ${o.color} rounded-3xl h-40 flex flex-col items-center justify-center text-white font-black text-2xl shadow-xl hover:scale-105 transition`}
          >
            <o.icon className="w-10 h-10 mb-2" />
            {o.label}
          </button>
        ))}
      </div>
      <Button variant="ghost" onClick={onBack} className="mt-6 text-white/60"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
    </div>
  );
}

function CaptureScreen({ onCapture, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [snapped, setSnapped] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({
          // Lower res = faster upload = faster verification. Gemini needs ~320+px to work.
          video: { width: { ideal: 480 }, height: { ideal: 360 }, facingMode: "user" },
          audio: false,
        });
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          videoRef.current.onloadedmetadata = () => {
            videoRef.current.play().catch(() => {});
            // 200ms after sensor is ready = safe to snap
            setTimeout(() => setVideoReady(true), 200);
          };
        }
      } catch (e) {
        toast.error("Camera access denied");
        onCancel();
      }
    })();
    return () => streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  const snap = () => {
    if (snapped || !videoReady) return;
    const v = videoRef.current;
    if (!v || !v.videoWidth) return;
    setSnapped(true);
    const canvas = document.createElement("canvas");
    // Downscale to 480x360 max — still plenty for Gemini
    const scale = Math.min(1, 480 / v.videoWidth);
    canvas.width = Math.round(v.videoWidth * scale);
    canvas.height = Math.round(v.videoHeight * scale);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    // 0.65 quality is visibly identical but ~40% smaller upload
    const dataUrl = canvas.toDataURL("image/jpeg", 0.65);
    onCapture(dataUrl);
  };

  // Auto-snap 800ms after camera is ready — most employees will just look at the screen.
  // If they want manual control they can tap the giant SNAP button.
  useEffect(() => {
    if (!videoReady || snapped) return;
    const t = setTimeout(snap, 800);
    return () => clearTimeout(t);
  }, [videoReady, snapped]);

  return (
    <div className="text-center max-w-xl w-full">
      <h2 className="text-2xl font-black mb-3">Look at the camera</h2>
      <div className="relative aspect-video bg-black rounded-3xl overflow-hidden border-4 border-amber-400/40 shadow-2xl">
        <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
        {snapped && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-16 h-16 text-black animate-spin" />
          </div>
        )}
        {!videoReady && !snapped && (
          <div className="absolute inset-0 flex items-center justify-center">
            <Loader2 className="w-12 h-12 text-white/70 animate-spin" />
          </div>
        )}
      </div>
      <div className="mt-4 flex gap-3 justify-center">
        <Button
          onClick={snap}
          disabled={!videoReady || snapped}
          data-testid="kiosk-snap"
          className="bg-amber-400 hover:bg-amber-500 text-black text-xl font-black h-16 px-10 rounded-2xl shadow-lg disabled:opacity-40"
        >
          <Camera className="w-6 h-6 mr-2" /> SNAP
        </Button>
        <Button variant="ghost" onClick={onCancel} className="text-white/60 h-16">Cancel</Button>
      </div>
      <p className="text-white/50 text-xs mt-2">
        {!videoReady ? "Camera starting…" : snapped ? "Verifying…" : "Tap SNAP or wait — auto-captures in a second"}
      </p>
    </div>
  );
}

function VerifyingScreen() {
  return (
    <div className="text-center">
      <Loader2 className="w-20 h-20 text-amber-400 animate-spin mx-auto mb-6" />
      <h2 className="text-3xl font-black">Verifying face…</h2>
      <p className="text-white/60 mt-2">Gemini AI is matching your face against your baseline photos</p>
    </div>
  );
}

function ResultScreen({ result, code, punchType, onRetry, onOverride }) {
  if (!result) return null;
  if (result.success) {
    return (
      <div className="text-center max-w-md">
        <div className="w-32 h-32 rounded-full bg-emerald-500/20 border-4 border-emerald-400 flex items-center justify-center mx-auto mb-6">
          <CheckCircle2 className="w-20 h-20 text-emerald-400" />
        </div>
        <h1 className="text-4xl font-black text-emerald-300 mb-2">SUCCESS</h1>
        <p className="text-2xl text-white">{result.message}</p>
        <p className="text-white/60 text-sm mt-3">Face confidence: {result.confidence}/10</p>
      </div>
    );
  }
  return (
    <div className="text-center max-w-md">
      <div className="w-32 h-32 rounded-full bg-red-500/20 border-4 border-red-400 flex items-center justify-center mx-auto mb-6">
        <XCircle className="w-20 h-20 text-red-400" />
      </div>
      <h1 className="text-4xl font-black text-red-400 mb-2">BLOCKED</h1>
      <p className="text-xl text-white/90">{result.message}</p>
      {result.confidence !== undefined && (
        <p className="text-white/50 text-xs mt-3">Face confidence: {result.confidence}/10 (need 5+)</p>
      )}
      <div className="mt-6 flex gap-3 justify-center">
        <Button onClick={onRetry} className="bg-amber-400 text-black hover:bg-amber-500" data-testid="kiosk-retry">
          Try Again
        </Button>
        <Button variant="outline" onClick={onOverride} className="border-red-400/50 text-red-300 hover:bg-red-500/10" data-testid="kiosk-override">
          <Shield className="w-4 h-4 mr-1" /> Manager Override
        </Button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// Modals
// ═══════════════════════════════════════════════════════════════

function ManagerOverrideModal({ code, punchType, selfie, onDone, onClose }) {
  const [pin, setPin] = useState("");
  const [reason, setReason] = useState("Face recognition failed — approved by manager");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (pin.length < 4) return toast.error("Enter master PIN");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/kiosk-face/punch/manager-override`, {
        code, punch_type: punchType, master_pin: pin, photo_base64: selfie, reason,
      });
      toast.success(res.data.message);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Override failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <Card className="bg-[#1A1620] border-red-400/30 p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <AlertTriangle className="w-8 h-8 text-red-400" />
          <div>
            <h2 className="text-xl font-black text-white">Manager Override</h2>
            <p className="text-white/60 text-xs">Only use when you personally verified the employee's identity.</p>
          </div>
        </div>
        <label className="text-white/70 text-xs font-bold uppercase tracking-wider">Master PIN</label>
        <input
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          data-testid="override-pin"
          className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest font-mono"
        />
        <label className="text-white/70 text-xs font-bold uppercase tracking-wider mt-4 block">Reason (logged)</label>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="override-reason"
          className="mt-1 w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
        />
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-red-500 text-white hover:bg-red-600" data-testid="override-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Override & Punch"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

function ExitModal({ onClose }) {
  const [pin, setPin] = useState("");
  const submit = () => {
    if (pin === "83528" || pin === "4817" || pin === "1111") {
      window.location.href = "/timecard";
    } else {
      toast.error("Wrong PIN");
      setPin("");
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur flex items-center justify-center p-4">
      <Card className="bg-[#1A1620] border-white/10 p-6 max-w-sm w-full">
        <h2 className="text-white font-black text-xl mb-3">Exit Kiosk?</h2>
        <p className="text-white/60 text-sm mb-4">Enter master PIN to leave locked kiosk mode.</p>
        <input
          type="password"
          inputMode="numeric"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          data-testid="exit-pin"
          className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white text-center text-2xl tracking-widest font-mono"
          autoFocus
        />
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="text-white/60">Cancel</Button>
          <Button onClick={submit} className="bg-amber-400 text-black hover:bg-amber-500" data-testid="exit-submit">Unlock</Button>
        </div>
      </Card>
    </div>
  );
}
